import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Spinner } from './Spinner.js';
import './Button.css';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'danger'
  | 'danger-outline'
  | 'link'
  /**
   * Filled success and warning. Added in Phase 10, because the prototype uses them as
   * **page-level primaries** and the catalogue could not express it: "Approve 1,880
   * merges" is filled green, "Approve and request sign-off" is filled amber, "Looks
   * right — release 11,390" is filled green. Rendering those as `primary` said
   * "continue"; the design says *what kind* of continue.
   *
   * They are not decorative alternatives to `primary` — pick one only when the act
   * itself is the tone: green for confirming something reversible has gone well, amber
   * for proceeding with a caveat.
   */
  | 'success'
  | 'warning';
/**
 * `xl` (44px) is the prototype's wizard/modal primary — the same height as a form
 * field, which is what makes an advance button look like the end of the form rather
 * than a control floating after it.
 */
export type ButtonSize = 'sm' | 'md' | 'lg' | 'xl';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'disabled'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shows a spinner and disables interaction, without changing the button's
   * label — per design-system/patterns.md's mandatory state set, loading is
   * a first-class state, not an afterthought. */
  loading?: boolean;
  disabled?: boolean;
}

/**
 * Primary/secondary/ghost/danger/link × 3 sizes, per
 * docs/design-system/components.md Tier 1. Primary/destructive intent is
 * distinguished by variant AND label text together, never colour alone —
 * a11y.md §5 ("primary vs. destructive actions are distinguished by label,
 * not colour alone").
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading = false, disabled = false, children, className, ...rest },
  ref
) {
  const isDisabled = disabled || loading;
  return (
    <button
      ref={ref}
      type={rest.type ?? 'button'}
      className={['dpr-button', `dpr-button--${variant}`, `dpr-button--${size}`, className].filter(Boolean).join(' ')}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <Spinner size={size === 'sm' ? 'sm' : 'md'} label="Loading" /> : null}
      {children}
    </button>
  );
});
